import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Check, ChevronDown, Globe, Search } from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function CountrySelect({ value, onChange, placeholder = "Select country", className = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    // Handle click outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = countries.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [search, countries]);

  useEffect(() => {
    // Set initial selected country based on value
    if (value && countries.length > 0) {
      const country = countries.find(c => c.name === value || c.code === value);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value, countries]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/reference/countries`);
      setCountries(response.data);
      setFilteredCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const handleSelect = (country) => {
    setSelectedCountry(country);
    onChange(country.name, country.code);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className="flex items-center justify-between w-full px-3 py-2 text-sm bg-background border rounded-md cursor-pointer hover:border-primary/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="country-select-trigger"
      >
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className={selectedCountry ? 'text-foreground' : 'text-muted-foreground'}>
            {selectedCountry ? selectedCountry.name : placeholder}
          </span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-64 overflow-hidden">
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search countries..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8"
                autoFocus
                data-testid="country-search-input"
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-48">
            {filteredCountries.length === 0 ? (
              <div className="p-3 text-sm text-muted-foreground text-center">No countries found</div>
            ) : (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-accent transition-colors ${
                    selectedCountry?.code === country.code ? 'bg-accent' : ''
                  }`}
                  onClick={() => handleSelect(country)}
                  data-testid={`country-option-${country.code}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{country.code}</span>
                    <span>{country.name}</span>
                  </div>
                  {selectedCountry?.code === country.code && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
